import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import Page from "components/page/Page";

import { StatusCode } from "api/protocols";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import Sheet from "components/sheet/Sheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select } from "components/select/index";
import { ProductCards } from "components/productCards/ProductCards";
import { IProduct, ICategories, IDepartments } from "redux/reducers/models/product";
import { isEqual } from "lodash";
import { IUIActions } from "redux/actions/ui";
import Popup from "components/popup/Popup";
import Toggle from "components/form/toggle/Toggle";
import { useTranslation } from "react-i18next";

import {
  DeleteFolder,
  DuplicateFolder,
  GetFolder,
  IFolder,
  IPostFolderProduct,
  PostFolderProducts,
  PutFolder,
} from "api/rpc/2024-04/facilityAdmin/product/folder";
import { GetRegisterGroups } from "api/rpc/2024-04/facilityAdmin/facility/register/group";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import {
  appendUniqueProducts,
  buildSelectAllProductBatch,
  fetchFolderProductSearchPage,
  IFolderProductSearchContext,
  sliceSelectAllBatch,
} from "components/folders/folderProductSearch";
import { faSmile } from "@fortawesome/pro-regular-svg-icons";
import EmojiGrid, { allIcons } from "components/IconSelector/iconSelector";
import Portal from "elements/Portal";
import { applyFolderProductPositionChange } from "helpers/folderProductDrag";

interface IFoldersEditProps {
  uiActions: IUIActions;
}

interface IFolderState {
  id: number;
  title: string;
  register_group_id: number;
  default: boolean;
  icon_name: string;
  folder_type: "green_fee" | "power_cart" | null;
}

interface IFilterState {
  search: string;
  browseFilter: IBrowseFilter;
}

type IBrowseDepartment = IDepartments & { cateogries?: ICategories[] };

export interface IProductState {
  productSearchResults: IProduct[] | undefined;
  productSearchTotalCount: number | undefined;
  productsToAddToFolder: IProduct[];
  folderProducts: IProduct[];
  availableProductPositions: number[];
  totalEmptySlotCount: number;
  productSearchPending: boolean;
  productSearchLoadingMore: boolean;
  productSearchSelectAllPending: boolean;
}

interface IUnsavedChangesState {
  folderProductsBeforeChanges: IProduct[];
  folderProductsLoaded: boolean;
  folderStateBeforeChanges: IFolderState;
  folderStateLoaded: boolean;
}

interface IRegisterState {
  registers: Array<Record<string, any>>;
}

interface IModalVisibilityState {
  deleteModalVisibility: boolean;
  duplicateModalVisibility: boolean;
  addProductsVisibility: boolean;
  saveEmptyFolderVisibility: boolean;
}

interface INewIconState {
  isModalOpen: boolean;
  icon_name: string;
}

export default function FoldersEdit(props: IFoldersEditProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const { id } = useParams<{ id: string }>();

  const initialFolderState: IFolderState = {
    id: undefined,
    title: "",
    register_group_id: undefined,
    default: false,
    icon_name: "",
    folder_type: null,
  };

  const [iconState, setIconState] = useState<INewIconState>({
    isModalOpen: false,
    icon_name: "",
  });

  const initialProductState: IProductState = {
    productSearchResults: undefined,
    productSearchTotalCount: undefined,
    productsToAddToFolder: [],
    folderProducts: [],
    availableProductPositions: [],
    totalEmptySlotCount: 0,
    productSearchPending: false,
    productSearchLoadingMore: false,
    productSearchSelectAllPending: false,
  };

  const initialUnsavedChangesState: IUnsavedChangesState = {
    folderProductsBeforeChanges: undefined,
    folderProductsLoaded: false,
    folderStateBeforeChanges: undefined,
    folderStateLoaded: false,
  };

  const initialRegisterState: IRegisterState = {
    registers: [],
  };

  const initialModalVisibilityState: IModalVisibilityState = {
    deleteModalVisibility: false,
    duplicateModalVisibility: false,
    addProductsVisibility: false,
    saveEmptyFolderVisibility: false,
  };

  const initialFilterState: IFilterState = {
    search: "",
    browseFilter: defaultBrowseFilter,
  };

  const [folderState, setFolderState] = useState<IFolderState>(initialFolderState);
  const [productState, setProductState] = useState<IProductState>(initialProductState);
  const [unsavedChangesState, setUnsavedChangesState] = useState<IUnsavedChangesState>(initialUnsavedChangesState);
  const [registerState, setRegisterState] = useState<IRegisterState>(initialRegisterState);
  const [modalVisibilityState, setModalVisibilityState] = useState<IModalVisibilityState>(initialModalVisibilityState);
  const [filterState, setFilterState] = useState<IFilterState>(initialFilterState);
  const [browseDepartments, setBrowseDepartments] = useState<IBrowseDepartment[]>([]);
  const [browseMetadataLoading, setBrowseMetadataLoading] = useState(false);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [existingFolders, setExistingFolders] = useState<IFolder[]>([]);
  const browseFilterRef = useRef(initialFilterState.browseFilter);
  const prevAddProductsModalOpen = useRef(false);
  const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  useEffect(() => {
    setFolderState(initialFolderState);
    setProductState(prev => ({ ...initialProductState, productSearchResults: prev.productSearchResults }));
    setUnsavedChangesState(initialUnsavedChangesState);
    setRegisterState(initialRegisterState);
    setModalVisibilityState(initialModalVisibilityState);
    setFilterState(initialFilterState);

    void loadFoldersProducts();
    void loadExistingFolders();
    void loadRegisters();
  }, [id]);

  async function loadExistingFolders() {
    const foldersRes = await GetFolder({ extended: false }, true);
    if (foldersRes.status !== StatusCode.OK) {
      return;
    }

    setExistingFolders(foldersRes.data ?? []);
  }

  function folderTypeTaken(type: "green_fee" | "power_cart") {
    return existingFolders.some(folder => folder.type === type && folder.id !== Number(id));
  }

  useEffect(() => {
    if (!modalVisibilityState.addProductsVisibility) {
      productSearchCancelTokenRef.current?.cancel();
      setProductState(prevState => ({
        ...prevState,
        productSearchPending: false,
        productSearchLoadingMore: false,
      }));
      prevAddProductsModalOpen.current = false;
      return;
    }

    const justOpened = !prevAddProductsModalOpen.current;
    prevAddProductsModalOpen.current = true;

    const browseChanged =
      browseFilterRef.current.type !== filterState.browseFilter.type ||
      browseFilterRef.current.id !== filterState.browseFilter.id;
    browseFilterRef.current = filterState.browseFilter;
    const delay = browseChanged ? 0 : 1000;

    let mounted = true;
    productSearchCancelTokenRef.current?.cancel();
    const cancelSource = axios.CancelToken.source();
    productSearchCancelTokenRef.current = cancelSource;

    setProductState(prevState => {
      const hasLoadedResults = (prevState.productSearchResults?.length ?? 0) > 0;

      return {
        ...prevState,
        productSearchPending: true,
        productSearchLoadingMore: false,
        ...(hasLoadedResults ? {} : { productSearchResults: undefined, productSearchTotalCount: undefined }),
      };
    });

    const runFetch = async () => {
      try {
        const { products, totalCount } = await fetchFolderProductSearchPage(
          GetProduct,
          getProductSearchContext(),
          0,
          true,
          cancelSource.token,
          () => props.uiActions.showError(t("Failed to get products")),
        );
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: products,
            productSearchTotalCount: totalCount,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        console.log("err", error);
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: [],
            productSearchTotalCount: 0,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (justOpened) {
      void runFetch();
    } else {
      timeoutId = global.setTimeout(() => {
        void runFetch();
      }, delay);
    }

    return () => {
      mounted = false;
      cancelSource.cancel();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [filterState.search, filterState.browseFilter, modalVisibilityState.addProductsVisibility]);

  async function loadBrowseMetadata() {
    const showLoading = browseDepartments.length === 0;

    if (showLoading) {
      setBrowseMetadataLoading(true);
    }

    try {
      const [departmentRes, countRes] = await Promise.all([
        GetDepartments({ extended: true }, false),
        GetProduct(
          {
            get_count: true,
            limit: 1,
            filters: [{ label: "uncategorized", value: [1] }],
          },
          false,
        ),
      ]);

      if (departmentRes.status === StatusCode.OK) {
        setBrowseDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
      }

      if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
        const countData = countRes.data as { count?: number };
        setUncategorizedCount(countData.count ?? 0);
      }
    } finally {
      if (showLoading) {
        setBrowseMetadataLoading(false);
      }
    }
  }

  function getProductSearchContext(): IFolderProductSearchContext {
    return {
      search: filterState.search,
      browseFilter: filterState.browseFilter,
    };
  }

  async function loadMoreProducts() {
    let shouldLoad = false;
    let offset = 0;

    setProductState(prev => {
      if (
        prev.productSearchLoadingMore ||
        prev.productSearchPending ||
        !prev.productSearchResults ||
        prev.productSearchTotalCount == null ||
        prev.productSearchResults.length >= prev.productSearchTotalCount
      ) {
        return prev;
      }

      shouldLoad = true;
      offset = prev.productSearchResults.length;
      return { ...prev, productSearchLoadingMore: true };
    });

    if (!shouldLoad) {
      return;
    }

    try {
      const { products } = await fetchFolderProductSearchPage(
        GetProduct,
        getProductSearchContext(),
        offset,
        false,
        undefined,
        () => props.uiActions.showError(t("Failed to get products")),
      );
      setProductState(prev => ({
        ...prev,
        productSearchResults: appendUniqueProducts(prev.productSearchResults ?? [], products),
        productSearchLoadingMore: false,
      }));
    } catch {
      setProductState(prev => ({ ...prev, productSearchLoadingMore: false }));
    }
  }

  async function selectAllMatchingProducts() {
    const loaded = productState.productSearchResults ?? [];
    const totalCount = productState.productSearchTotalCount ?? loaded.length;
    const remaining = productState.availableProductPositions.length - productState.productsToAddToFolder.length;
    const excludeIds = new Set([
      ...productState.folderProducts.map(product => product.id),
      ...productState.productsToAddToFolder.map(product => product.id),
    ]);
    const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;

    if (needsRemoteFetch) {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: true }));
    }

    try {
      const toAdd = await buildSelectAllProductBatch(
        GetProduct,
        getProductSearchContext(),
        loaded,
        totalCount,
        excludeIds,
        remaining,
        () => props.uiActions.showError(t("Failed to get products")),
      );

      setProductState(prev => {
        const batch = sliceSelectAllBatch(toAdd, prev.productsToAddToFolder, prev.availableProductPositions.length);

        if (batch.length === 0) {
          return { ...prev, productSearchSelectAllPending: false };
        }

        return {
          ...prev,
          productsToAddToFolder: [...prev.productsToAddToFolder, ...batch],
          productSearchSelectAllPending: false,
        };
      });
    } catch {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: false }));
    }
  }

  async function loadFoldersProducts() {
    const resFolder = await GetFolder({ id: Number(id), extended: true }, true);

    if (resFolder.status !== StatusCode.OK) {
      props.uiActions.showError(t("Failed to get folder"));
      return;
    }

    setProductState(prev => ({
      ...prev,
      folderProducts: resFolder.data[0].products
        .filter((product: any) => {
          return product.folder_position !== undefined && product.folder_position !== null;
        })
        .sort((prev: any, next: any) => prev.folder_position - next.folder_position),
    }));

    setFolderState(prev => ({
      ...prev,
      id: resFolder.data[0].id,
      title: resFolder.data[0].title,
      register_group_id: resFolder.data[0]?.register_group_id,
      default: resFolder.data[0].default,
      icon_name: resFolder.data[0].icon_name,
      folder_type: (resFolder.data[0] as any)?.type ?? null,
    }));

    setUnsavedChangesState(prev => ({
      ...prev,
      folderProductsLoaded: true,
      folderStateLoaded: true,
    }));
  }

  async function saveFolder() {
    if (folderState.folder_type !== null && folderTypeTaken(folderState.folder_type)) {
      props.uiActions.showError(t("A folder of this type already exists"));
      return false;
    }

    const params = {
      id: folderState.id,
      title: folderState.title,
      register_group_id: folderState.register_group_id,
      default: folderState.default,
      icon_name: folderState.icon_name,
      type: folderState.folder_type,
    };

    const putFolderRes = await PutFolder(params, true);

    if (putFolderRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("Failed to get update folder"));
      return false;
    }

    setUnsavedChangesState(prev => ({
      ...prev,
      folderStateBeforeChanges: folderState,
    }));

    const products: IPostFolderProduct[] = [];
    for (let i = 0; i < productState.folderProducts.length; i++) {
      const product = productState.folderProducts[i];
      products.push({
        id: product.id,
        position: product.folder_position,
        button_color: product.meta?.button_color,
      });
    }

    const postFolderProductsRes = await PostFolderProducts({ folder_id: Number(id), products }, true);

    if (postFolderProductsRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("Failed to update folder products"));
      return false;
    }

    setUnsavedChangesState(prev => ({
      ...prev,
      folderProductsBeforeChanges: productState.folderProducts,
    }));

    return true;
  }

  function saveFolderHandler() {
    if (productState.folderProducts.length === 0 && unsavedChangesState.folderProductsBeforeChanges.length > 0) {
      openSaveEmptyFolderModal();
    } else {
      void saveFolder();
    }
  }

  function openSaveEmptyFolderModal() {
    setModalVisibilityState(prev => ({
      ...prev,
      saveEmptyFolderVisibility: true,
    }));
  }

  function closeSaveEmptyFolderModal() {
    setModalVisibilityState(prev => ({
      ...prev,
      saveEmptyFolderVisibility: false,
    }));
  }

  async function handleSaveEmptyFolderModal() {
    const saveSuccessful = await saveFolder();

    if (saveSuccessful) {
      closeSaveEmptyFolderModal();
    }
  }

  async function removeFolder() {
    const res = await DeleteFolder(folderState, true);

    if (res.status !== Number(StatusCode.OK)) {
      props.uiActions.showError(t("Failed to delete folder"));
      return;
    }

    history.push("/admin/settings/folders");
  }

  async function duplicateFolder() {
    const res = await DuplicateFolder({ folder_id: parseInt(id) }, true);

    if (res.status !== Number(StatusCode.OK)) {
      props.uiActions.showError(t("Failed to duplicate folder"));
      return;
    }

    history.push(`/admin/settings/folders/${res.data?.id}`);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setFolderState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDeleteModalVisibility() {
    setModalVisibilityState(prev => ({
      ...prev,
      deleteModalVisibility: !prev.deleteModalVisibility,
    }));
  }

  function handleDuplicateModalVisibility() {
    setModalVisibilityState(prev => ({
      ...prev,
      duplicateModalVisibility: !prev.duplicateModalVisibility,
    }));
  }

  function handleOpenAddProductsModal(availableProductPositions: number[], totalEmptySlotCount: number) {
    setModalVisibilityState(prev => ({
      ...prev,
      addProductsVisibility: !prev.addProductsVisibility,
    }));
    setProductState(prev => ({
      ...prev,
      availableProductPositions,
      totalEmptySlotCount,
      productSearchPending: true,
      productSearchResults: undefined,
      productSearchTotalCount: undefined,
    }));
    setFilterState({ search: "", browseFilter: defaultBrowseFilter });
    void loadBrowseMetadata();
  }

  function handleAddProductsSearchChange(value: string) {
    setFilterState(prevState => ({ ...prevState, search: value }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function handleAddProductsBrowseFilterChange(browseFilter: IBrowseFilter) {
    setFilterState(prevState => ({ ...prevState, browseFilter }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function handleCloseAddProductsModal() {
    setModalVisibilityState(prev => ({
      ...prev,
      addProductsVisibility: !prev.addProductsVisibility,
    }));
    setFilterState({ search: "", browseFilter: defaultBrowseFilter });
    setProductState(prev => ({
      ...prev,
      productsToAddToFolder: [],
      productSearchResults: filterState.search === "" ? prev.productSearchResults : [],
      availableProductPositions: [],
      totalEmptySlotCount: 0,
    }));
  }

  function handleRemoveFolderProduct(id: number) {
    const updatedFolderProducts = productState.folderProducts.filter(product => product.id !== id);

    setProductState(prev => ({
      ...prev,
      folderProducts: updatedFolderProducts,
    }));
  }

  function handleSelectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

    setProductState(prev => {
      const selectedIds = new Set(prev.productsToAddToFolder.map(productToAdd => productToAdd.id));
      const remaining = prev.availableProductPositions.length - prev.productsToAddToFolder.length;
      const toAdd = products.filter(product => !selectedIds.has(product.id)).slice(0, remaining);

      if (toAdd.length === 0) {
        return prev;
      }

      return { ...prev, productsToAddToFolder: [...prev.productsToAddToFolder, ...toAdd] };
    });
  }

  function handleUnselectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
    const idsToRemove = new Set(products.map(product => product.id));

    setProductState(prev => ({
      ...prev,
      productsToAddToFolder: prev.productsToAddToFolder.filter(productToAdd => !idsToRemove.has(productToAdd.id)),
    }));
  }

  function handleChangeFolderProductPosition(id: number, position: number) {
    setProductState(prev => ({
      ...prev,
      folderProducts: applyFolderProductPositionChange(prev.folderProducts, id, position),
    }));
  }

  function addFolderProducts(productsToAdd: IProduct[]) {
    const updatedProductsToAddToFolder: IProduct[] = [];

    for (
      let i = 0;
      i < Math.min(productsToAdd.length, productState.availableProductPositions.length);
      i++
    ) {
      updatedProductsToAddToFolder.push({
        ...productsToAdd[i],
        folder_position: productState.availableProductPositions[i],
      });
    }

    setProductState(prev => ({ ...prev, folderProducts: [...prev.folderProducts, ...updatedProductsToAddToFolder] }));
    handleCloseAddProductsModal();
  }

  function setFolderProducts(products: IProduct[]) {
    setProductState(prev => ({ ...prev, folderProducts: products }));
  }

  async function loadRegisters() {
    const registersRes = await GetRegisterGroups(null, true);
    if (registersRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("Failed to load registers"));
      return;
    }

    setRegisterState(prev => ({ ...prev, registers: registersRes.data }));
  }

  function handleRegisterSelect(register_group_id: number) {
    setFolderState(prevState => ({ ...prevState, register_group_id }));
  }

  function handleFolderTypeSelect(folder_type: "green_fee" | "power_cart" | "") {
    setFolderState(prevState => ({ ...prevState, folder_type: folder_type === "" ? null : folder_type }));
  }

  function unsavedChangesExist() {
    if (
      unsavedChangesState.folderProductsBeforeChanges === undefined ||
      unsavedChangesState.folderStateBeforeChanges === undefined
    ) {
      if (unsavedChangesState.folderProductsLoaded && unsavedChangesState.folderStateLoaded) {
        setUnsavedChangesState(prev => ({
          ...prev,
          folderProductsBeforeChanges: productState.folderProducts,
          folderStateBeforeChanges: folderState,
        }));
      }
      return false;
    }

    // Only should compare product properties that are being saved
    // GetProduct and GetFolder return slightly different data
    const productToPost = (product: IProduct): IPostFolderProduct => ({
      id: product.id,
      position: product.folder_position,
      button_color: product.meta?.button_color,
    });

    const postFolderProducts: IPostFolderProduct[] = productState.folderProducts.map(productToPost);
    const postFolderProductsBeforeChanges: IPostFolderProduct[] =
      unsavedChangesState.folderProductsBeforeChanges.map(productToPost);

    return (
      !isEqual(postFolderProductsBeforeChanges, postFolderProducts) ||
      !isEqual(unsavedChangesState.folderStateBeforeChanges, folderState)
    );
  }

  function cancelUnsavedChanges() {
    setFolderState(unsavedChangesState.folderStateBeforeChanges);
    setProductState(prev => ({ ...prev, folderProducts: unsavedChangesState.folderProductsBeforeChanges }));
  }

  function handleToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const id = event?.target?.id;
    const checked = event?.target?.checked;

    if (id === undefined || checked === undefined) {
      return;
    }

    setFolderState(prev => {
      return { ...prev, default: checked };
    });
  }

  function openingIconModal() {
    setIconState(prevState => ({ ...prevState, isModalOpen: true }));
  }

  const secondaryAction = {
    content: (
      <>
        <FontAwesomeIcon icon={faSmile} style={{ marginRight: "8px" }} /> {/* Smile icon */}
        {t("Add Icon")}
      </>
    ),
    action: () => openingIconModal(),
  };

  // This function is passed to EmojiGrid to handle icon selection
  function handleIconSelect(icon: string) {
    setIconState(prevState => ({ ...prevState, icon_name: icon }));
  }

  function handleIconConfirm() {
    setFolderState(prevState => ({ ...prevState, icon_name: iconState.icon_name }));
    setIconState(prevState => ({ ...prevState, isModalOpen: false, icon_name: "" }));
  }

  return (
    <div>
      {unsavedChangesState.folderProductsLoaded && unsavedChangesState.folderStateLoaded && (
        <>
          <Page
            title={t("Edit Folder: ") + (unsavedChangesState.folderStateBeforeChanges?.title ?? "")}
            secondaryActions={[secondaryAction]} // Add secondary action here
            titleMetadata={
              folderState.icon_name && (
                <FontAwesomeIcon icon={allIcons[folderState.icon_name]} style={{ marginRight: "8px" }} />
              )
            }
            narrow
            // primaryAction={deleteAction}
            // secondaryActions={[duplicateAction]}
            multipleActionDropdownAction={{
              label: t("Options"),
              dropdownProps: {
                alignment: "right",
                options: [
                  {
                    type: "handler",
                    label: t("Delete"),
                    icon: "trash",
                    handler: handleDeleteModalVisibility,
                  },
                  {
                    type: "handler",
                    label: t("Duplicate"),
                    handler: handleDuplicateModalVisibility,
                    icon: "copy",
                  },
                ],
              },
            }}
            breadcrumbs={[
              {
                prefix: true,
                label: t("Folders"),
                url: "/admin/settings/folders",
              },
            ]}
            notificationBarProps={{
              isVisible:
                unsavedChangesExist() &&
                !modalVisibilityState.addProductsVisibility &&
                !modalVisibilityState.saveEmptyFolderVisibility,
              onAction: saveFolderHandler,
              onCancel: cancelUnsavedChanges,
            }}
          >
            <Portal isMounted={iconState.isModalOpen}>
              <Sheet
                title={t("Add Icon")}
                supportText={t("Choose an icon to help visually identify this folder category")}
                size="medium"
                open={iconState.isModalOpen}
                onOk={handleIconConfirm}
                okText={t("Update")}
                closable
                onCancel={() => setIconState(prevState => ({ ...prevState, isModalOpen: false, icon_name: "" }))}
              >
                <EmojiGrid onIconSelect={handleIconSelect} selectedIcon={folderState.icon_name} />
              </Sheet>
            </Portal>

            <Card>
              <Card.Section>
                <Input
                  value={folderState.title}
                  label={t("Title")}
                  id="title"
                  onChange={handleInputChange}
                  placeholder={t("Title")}
                  className="mb-2"
                />
                <Select
                  label={t("Register Group")}
                  onChange={handleRegisterSelect}
                  defaultValue={folderState.register_group_id}
                >
                  {registerState.registers?.map((register, index) => {
                    return (
                      <Option value={register?.id} name={register?.title} key={index}>
                        <span>{register?.title}</span>
                      </Option>
                    );
                  })}
                </Select>

                <div className="mt-4">
                  <Toggle
                    labelRight={t("Default")}
                    id="default_folder"
                    onChange={handleToggleChange}
                    checked={folderState.default}
                    size="medium"
                  />
                </div>
              </Card.Section>

              <Card.Section
                title={t("Folder Type")}
                subtitle={t(
                  "If this folder is specific to checking in a green fee or power cart, select the type here.",
                )}
              >
                <Select
                  label={t("Type")}
                  onChange={handleFolderTypeSelect}
                  defaultValue={folderState.folder_type ?? ""}
                >
                  <Option value="" name={t("None")}>
                    {t("None")}
                  </Option>
                  <Option value="green_fee" name={t("Green Fee")} disabled={folderTypeTaken("green_fee")}>
                    {t("Green Fee")}
                  </Option>
                  <Option value="power_cart" name={t("Power Cart")} disabled={folderTypeTaken("power_cart")}>
                    {t("Power Cart")}
                  </Option>
                </Select>
              </Card.Section>
              <Card.Section>
                <ProductCards
                  products={productState.folderProducts}
                  productsLoaded={unsavedChangesState.folderProductsLoaded}
                  onChangeProductPosition={handleChangeFolderProductPosition}
                  onRemoveProduct={handleRemoveFolderProduct}
                  onAddProducts={handleOpenAddProductsModal}
                  setProducts={setFolderProducts}
                />
              </Card.Section>
            </Card>
          </Page>

          <Popup
            open={modalVisibilityState.deleteModalVisibility}
            type="warning"
            title={t("Deletion Confirmation")}
            description={t("Are you sure you want to delete this folder?")}
            okText={t("Delete")}
            onOk={removeFolder}
            cancelText={t("Close")}
            onCancel={handleDeleteModalVisibility}
          />

          <Popup
            open={modalVisibilityState.duplicateModalVisibility}
            type="warning"
            title={t("Duplicate Confirmation")}
            description={t("Are you sure you want to duplicate this folder?")}
            okText={t("Duplicate")}
            onOk={duplicateFolder}
            cancelText={t("Close")}
            onCancel={handleDuplicateModalVisibility}
          />

          <Popup
            open={modalVisibilityState.saveEmptyFolderVisibility}
            type="warning"
            title={t("Removing All Products")}
            description={t("Are you sure you want to remove all of the products in this folder?")}
            okText={t("Remove")}
            onOk={handleSaveEmptyFolderModal}
            cancelText={t("Close")}
            onCancel={closeSaveEmptyFolderModal}
          />

          <AddFolderProductsModal
            open={modalVisibilityState.addProductsVisibility}
            onClose={handleCloseAddProductsModal}
            onAdd={addFolderProducts}
            search={filterState.search}
            onSearchChange={handleAddProductsSearchChange}
            browseFilter={filterState.browseFilter}
            onBrowseFilterChange={handleAddProductsBrowseFilterChange}
            departments={browseDepartments}
            departmentsLoading={browseMetadataLoading}
            uncategorizedCount={uncategorizedCount}
            productsToAdd={productState.productsToAddToFolder}
            availableSlotCount={productState.availableProductPositions.length}
            totalEmptySlotCount={productState.totalEmptySlotCount}
            folderProducts={productState.folderProducts}
            productSearchResults={productState.productSearchResults}
            productSearchTotalCount={productState.productSearchTotalCount}
            productSearchPending={productState.productSearchPending}
            productSearchLoadingMore={productState.productSearchLoadingMore}
            productSearchSelectAllPending={productState.productSearchSelectAllPending}
            onLoadMoreProducts={loadMoreProducts}
            onSelectAllMatchingProducts={selectAllMatchingProducts}
            onSelectProduct={handleSelectProductToAdd}
            onUnselectProduct={handleUnselectProductToAdd}
            onCreateProduct={() => {
              handleCloseAddProductsModal();
              history.push("/admin/product/new");
            }}
            onGoToProducts={() => {
              handleCloseAddProductsModal();
              history.push("/admin/product");
            }}
          />
        </>
      )}
    </div>
  );
}
